import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, RefreshCw, Trash2, Save, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CacheSettingsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [settings, setSettings] = useState({
    html_cache_enabled: true,
    html_cache_ttl_minutes: 5,
    image_cache_max_age_days: 7,
    '06se_cache_mode': 'direct'
  });
  
  const [cacheStatus, setCacheStatus] = useState({
    html_cache: { enabled: true, ttl_minutes: 5, file_count: 0, size_mb: 0 },
    image_cache: { max_age_days: 7, file_count: 0, size_mb: 0 }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchCacheSettings();
    fetchCacheStatus();
  }, []);

  const fetchCacheSettings = async () => {
    try {
      const response = await axios.get(`${API}/cache-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching cache settings:", error);
      toast.error("Failed to load cache settings");
    }
  };

  const fetchCacheStatus = async () => {
    try {
      const response = await axios.get(`${API}/cache/status`);
      setCacheStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching cache status:", error);
      toast.error("Failed to load cache status");
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/cache-settings`, settings);
      toast.success("Cache settings saved successfully!");
      fetchCacheStatus(); // Refresh status
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save cache settings");
    } finally {
      setSaving(false);
    }
  };

  const handleClearCache = async (source = null) => {
    setClearing(true);
    try {
      const url = source ? `${API}/cache/clear?source=${source}` : `${API}/cache/clear`;
      const response = await axios.post(url);
      toast.success(response.data.message);
      fetchCacheStatus(); // Refresh status
    } catch (error) {
      console.error("Error clearing cache:", error);
      toast.error("Failed to clear cache");
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading cache settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Cache Settings
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage HTML and image cache configuration
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-semibold mb-1">About Caching</p>
              <p>
                HTML caching stores website pages temporarily to improve loading speed.
                When enabled, the app will serve cached content instead of fetching fresh data on every request.
                Lower TTL (Time To Live) means more frequent updates but slower performance.
              </p>
            </div>
          </div>
        </div>

        {/* HTML Cache Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            HTML Cache Configuration
          </h2>

          {/* Enable/Disable HTML Cache */}
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <Label htmlFor="html-cache-enabled" className="text-base font-medium text-gray-900 dark:text-gray-100">
                Enable HTML Caching
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Cache article listings for faster loading
              </p>
            </div>
            <Switch
              id="html-cache-enabled"
              checked={settings.html_cache_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, html_cache_enabled: checked })}
            />
          </div>

          {/* Cache TTL Slider */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <Label htmlFor="ttl-slider" className="text-base font-medium text-gray-900 dark:text-gray-100">
                Cache Duration (TTL)
              </Label>
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                {settings.html_cache_ttl_minutes} minutes
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              How long cached content stays fresh before being refreshed
            </p>
            <Slider
              id="ttl-slider"
              min={1}
              max={60}
              step={1}
              value={[settings.html_cache_ttl_minutes]}
              onValueChange={(value) => setSettings({ ...settings, html_cache_ttl_minutes: value[0] })}
              disabled={!settings.html_cache_enabled}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-1">
              <span>1 min (Always fresh)</span>
              <span>30 min (Balanced)</span>
              <span>60 min (Max performance)</span>
            </div>
          </div>

          {/* HTML Cache Status */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Current HTML Cache Status</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Status</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.html_cache.enabled ? (
                    <span className="text-green-600 dark:text-green-400">✓ Enabled</span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400">✗ Disabled</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">TTL</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.html_cache.ttl_minutes} minutes
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Cached Files</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.html_cache.file_count} files
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Cache Size</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.html_cache.size_mb} MB
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Image Cache Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Image Cache Configuration
          </h2>

          {/* Max Age Slider */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <Label htmlFor="image-age-slider" className="text-base font-medium text-gray-900 dark:text-gray-100">
                Image Cache Max Age
              </Label>
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                {settings.image_cache_max_age_days} days
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Automatically delete cached images older than this duration
            </p>
            <Slider
              id="image-age-slider"
              min={1}
              max={30}
              step={1}
              value={[settings.image_cache_max_age_days]}
              onValueChange={(value) => setSettings({ ...settings, image_cache_max_age_days: value[0] })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-1">
              <span>1 day</span>
              <span>15 days</span>
              <span>30 days</span>
            </div>
          </div>

          {/* Image Cache Status */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Current Image Cache Status</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Cached Images</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.image_cache.file_count} files
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Cache Size</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.image_cache.size_mb} MB
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Max Age</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cacheStatus.image_cache.max_age_days} days
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 06se Image Loading Mode */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            06se.com Image Loading Mode
          </h2>

          {/* Info Banner */}
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-semibold mb-1">Choose loading strategy for 06se images:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Direct:</strong> Load images directly from 06se CDN (Fast, requires good network)</li>
                  <li><strong>Cache:</strong> Download and serve from local cache (Slower initial load, more stable)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="space-y-2">
            <Label htmlFor="06se-mode" className="text-base font-medium text-gray-900 dark:text-gray-100">
              Loading Mode
            </Label>
            <Select 
              value={settings['06se_cache_mode']} 
              onValueChange={(value) => setSettings({ ...settings, '06se_cache_mode': value })}
            >
              <SelectTrigger id="06se-mode">
                <SelectValue placeholder="Select loading mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">⚡</span>
                    <span>Direct (Fast)</span>
                  </div>
                </SelectItem>
                <SelectItem value="cache">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600">💾</span>
                    <span>Cache (Stable)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {settings['06se_cache_mode'] === 'direct' 
                ? '✓ Images load directly from 06se CDN. Faster but may fail if CDN blocks requests.'
                : '✓ Images are downloaded to server cache first. Slower but more reliable.'}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            size="lg"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>

          <Button
            onClick={() => handleClearCache()}
            disabled={clearing}
            variant="destructive"
            size="lg"
            className="flex-1"
          >
            {clearing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Clearing...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Cache
              </>
            )}
          </Button>
        </div>

        {/* Clear by Source */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Clear Cache by Source
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {['misskon', '4khd', '06se'].map((source) => (
              <Button
                key={source}
                onClick={() => handleClearCache(source)}
                disabled={clearing}
                variant="outline"
                size="sm"
                className="text-gray-700 dark:text-gray-300"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear {source}
              </Button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CacheSettingsPage;
